/**
 * Billing System Types
 *
 * TypeScript interfaces for the TerraMatrix billing system including
 * subscriptions, token vaults, credit transactions, and pricing tiers.
 */

export type SubscriptionTier = 'free' | 'basic' | 'standard' | 'pro' | 'enterprise';
export type BillingInterval = 'monthly' | 'annual';
export type SubscriptionStatus = 'active' | 'trialing' | 'past_due' | 'canceled' | 'suspended' | 'unpaid';
export type VaultType = 'team' | 'project' | 'personal' | 'general';
export type CreditTransactionType = 'plan_allocation' | 'plan_reset' | 'ai_usage' | 'extra_purchase' | 'extra_usage' | 'vault_reallocation' | 'refund';

export interface SubscriptionFeatures {
  visualEditor: boolean;
  workflowService: boolean;
  dataService: boolean;
  collaboration: boolean;
  multipleAdmins: boolean;
}

export interface Subscription {
  id: string;
  orgId: string;
  stripeCustomerId?: string | null;
  stripeSubscriptionId?: string | null;
  billingProvider?: 'stripe' | 'internal' | null;
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  billingInterval: BillingInterval;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  trialStartedAt?: string;
  trialEndsAt?: string;
  hasUsedTrial: boolean;
  planBudgetCents: number;
  freeAllowanceCents?: number;
  freeAllowanceUsedCents?: number;
  features: SubscriptionFeatures;
  maxMembers: number;
  maxAdmins: number;
  maxVaults: number;
}

function hasNonEmptyStripeId(value: string | null | undefined): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

export function isStripeManagedSubscription(
  subscription: Pick<
    Subscription,
    "billingProvider" | "stripeCustomerId" | "stripeSubscriptionId"
  > | null | undefined,
): boolean {
  if (!subscription) {
    return false;
  }

  if (subscription.billingProvider === "internal") {
    return false;
  }

  if (subscription.billingProvider === "stripe") {
    return true;
  }

  return (
    hasNonEmptyStripeId(subscription.stripeCustomerId) ||
    hasNonEmptyStripeId(subscription.stripeSubscriptionId)
  );
}

export interface TokenVault {
  id: string;
  orgId: string;
  name: string;
  type: VaultType;
  allocatedBudgetCents: number;
  usedBudgetCents: number;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  assignments: VaultAssignment[];
  allowFallthrough: boolean;
  isDefault: boolean;
  usagePercent: number; // computed
}

export interface VaultAssignment {
  targetType: 'user' | 'group' | 'app' | 'agent' | 'project' | 'page' | 'org';
  targetId: string;
  priority: number;
  assignedAt: string;
}

export interface CreditBalance {
  extraBalanceCents: number;
  totalExtraPurchasedCents: number;
}

export interface CreditTransaction {
  id: string;
  orgId: string;
  vaultId?: string;
  type: CreditTransactionType;
  amountCents: number;
  balanceAfterCents: number;
  metadata?: Record<string, unknown>;
  stripePaymentIntentId?: string;
  serviceFeeAppliedCents?: number;
  description?: string;
  createdAt: string;
}

export interface CreditPack {
  id: string;
  amount: number;
  creditValue: number;
  label: string;
}

export interface VaultUsage {
  vaultId: string;
  vaultName: string;
  type: VaultType;
  allocatedBudgetCents: number;
  usedBudgetCents: number;
  usagePercent: number;
  resetDate: string;
}

export interface Invoice {
  id: string;
  number: string;
  status: string;
  amountDue: number;
  amountPaid: number;
  currency: string;
  created: number;
  hostedInvoiceUrl?: string;
  invoicePdf?: string;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export const TIER_DISPLAY: Record<SubscriptionTier, { name: string; monthlyPrice: number; annualPrice: number; description: string }> = {
  free: { name: 'Free', monthlyPrice: 0, annualPrice: 0, description: 'For personal exploration' },
  basic: { name: 'Basic', monthlyPrice: 10, annualPrice: 100, description: 'For small teams getting started' },
  standard: { name: 'Standard', monthlyPrice: 30, annualPrice: 300, description: 'For growing teams' },
  pro: { name: 'Pro', monthlyPrice: 100, annualPrice: 1000, description: 'For professional teams' },
  enterprise: { name: 'Enterprise', monthlyPrice: 0, annualPrice: 0, description: 'Custom solutions for large organizations' },
};

export const CREDIT_PACKS: CreditPack[] = [
  { id: 'credits_10', amount: 1050, creditValue: 1000, label: '$10' },
  { id: 'credits_25', amount: 2625, creditValue: 2500, label: '$25' },
  { id: 'credits_50', amount: 5250, creditValue: 5000, label: '$50' },
  { id: 'credits_100', amount: 10500, creditValue: 10000, label: '$100' },
];

/** Tier feature matrix for comparison display */
export const TIER_FEATURES: Record<SubscriptionTier, {
  aiBudget: string;
  vaults: string;
  members: string;
  workflow: boolean;
  dataService: boolean;
  collaboration: boolean;
  multipleAdmins: boolean;
}> = {
  free: {
    aiBudget: '$2/mo',
    vaults: '0',
    members: '1',
    workflow: false,
    dataService: false,
    collaboration: false,
    multipleAdmins: false,
  },
  basic: {
    aiBudget: '$10/mo',
    vaults: '0',
    members: '15 access members / 15 sponsored sub-accounts',
    workflow: true,
    dataService: true,
    collaboration: true,
    multipleAdmins: false,
  },
  standard: {
    aiBudget: '$30/mo',
    vaults: '3',
    members: '35 access members / 30 sponsored sub-accounts',
    workflow: true,
    dataService: true,
    collaboration: true,
    multipleAdmins: false,
  },
  pro: {
    aiBudget: '$100/mo',
    vaults: '10',
    members: '75 access members / 50 sponsored sub-accounts',
    workflow: true,
    dataService: true,
    collaboration: true,
    multipleAdmins: true,
  },
  enterprise: {
    aiBudget: 'Custom',
    vaults: 'Unlimited',
    members: 'Unlimited',
    workflow: true,
    dataService: true,
    collaboration: true,
    multipleAdmins: true,
  },
};
